import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  SwitchRoot,
  SwitchThumb,
  Text,
  toast,
} from '@workwhile/ui'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ModalSelect, ModalWithSelect } from 'components/ModalSelect'
import { CompanyFilePicker } from '../../../components/CompanyFilePicker'
import { DeliveryHourInput } from '../../../components/DeliveryHourInput'
import {
  editIndeedFlexCompanyFlowSchema,
  EditIndeedFlexCompanyFlowSchema,
} from '../schemas/editCompany'
import { useEditIndeedFlexCompanyFlowContextValue } from '../context/EditIndeedFlexCompanyFlowProvider'
import { useIndeedFlexCompaniesWorkflowsContextValue } from '../../../context/IndeedFlexCompaniesWorkflowsProvider'
import { useIndeedFlexCompaniesContextValue } from '../../../context/IndeedFlexCompaniesProvider'

type EditIndeedFlexCompanyFlowMainProps = {
  company: NonNullable<
    ReturnType<
      typeof useIndeedFlexCompaniesWorkflowsContextValue
    >['editCompanyFlow']['company']
  >
}

export const EditIndeedFlexCompanyFlowMain = ({
  company,
}: EditIndeedFlexCompanyFlowMainProps) => {
  const {
    editCompanyFlow: { isOpen, close },
  } = useIndeedFlexCompaniesWorkflowsContextValue()
  const {
    state: { clients },
  } = useIndeedFlexCompaniesContextValue()
  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useEditIndeedFlexCompanyFlowContextValue()

  const initialClientRequiresCertificationType =
    clients.find((client) => client.ifClientId === company.ifClientId)
      ?.requiresCertificationType ?? false

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    register,
    setValue,
  } = useForm<EditIndeedFlexCompanyFlowSchema>({
    resolver: zodResolver(editIndeedFlexCompanyFlowSchema),
    values: {
      ifClientId: company.ifClientId,
      canCreateShifts: company.canCreateShifts,
      requiresSignedForm:
        company.fileHandleId != null || initialClientRequiresCertificationType,
      fileHandleId: company.fileHandleId ?? undefined,
      shiftNotificationTitle: company.shiftNotificationTitle ?? '',
      shiftNotificationMessage: company.shiftNotificationMessage ?? '',
      shiftNotificationDeliveryHour: company.shiftNotificationDeliveryHour,
    },
  })

  const ifClientId = useWatch({ control, name: 'ifClientId' })
  const requiresSignedForm = useWatch({
    control,
    name: 'requiresSignedForm',
  })

  const clientRequiresCertificationType =
    clients.find((client) => client.ifClientId === ifClientId)
      ?.requiresCertificationType ?? false

  useEffect(() => {
    if (clientRequiresCertificationType) {
      setValue('requiresSignedForm', true)
    }
  }, [clientRequiresCertificationType, setValue])

  const clientOptions = clients.map((client) => ({
    label: `${client.name} (${client.ifClientId})`,
    value: client.ifClientId,
  }))

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        indeedFlexCompanyId: company.id,
        ifClientId: data.ifClientId,
        canCreateShifts: data.canCreateShifts,
        fileHandleId: data.requiresSignedForm
          ? data.fileHandleId ?? null
          : null,
        shiftNotificationTitle: data.shiftNotificationTitle ?? null,
        shiftNotificationMessage: data.shiftNotificationMessage ?? null,
        shiftNotificationDeliveryHour:
          data.shiftNotificationDeliveryHour ?? null,
      },
      {
        onSuccess: () => {
          toast.success('Indeed Flex company updated', {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit Indeed Flex company
        </Heading>

        <Box mt={4}>
          <Field label="Company">
            <Text fontSize={2}>
              {company.companyName} ({company.companyId})
            </Text>
          </Field>
        </Box>

        <Box mt={3}>
          <Controller
            control={control}
            name="ifClientId"
            render={({ field }) => (
              <Field
                label="Indeed Flex client"
                error={errors.ifClientId?.message}
              >
                <ModalSelect
                  options={clientOptions}
                  value={
                    clientOptions.find(
                      (option) => option.value === field.value
                    ) || null
                  }
                  onChange={(option) =>
                    field.onChange(option?.value ?? undefined)
                  }
                  placeholder="Select a client"
                />
              </Field>
            )}
          />
        </Box>

        <Box mt={3}>
          <Field label="Create shifts automatically">
            <Controller
              control={control}
              name="canCreateShifts"
              render={({ field }) => (
                <SwitchRoot
                  checked={field.value}
                  onCheckedChange={(value) => field.onChange(value)}
                  aria-label="Create shifts automatically"
                >
                  <SwitchThumb>{field.value ? 'Yes' : 'No'}</SwitchThumb>
                </SwitchRoot>
              )}
            />
          </Field>
        </Box>

        <Box mt={3}>
          <Controller
            control={control}
            name="requiresSignedForm"
            render={({ field }) => (
              <Checkbox
                value={field.value}
                disabled={clientRequiresCertificationType}
                onChange={(checked) => {
                  if (clientRequiresCertificationType) return
                  field.onChange(checked)
                  if (!checked) {
                    setValue('fileHandleId', undefined)
                  }
                }}
              >
                Company requires signed form
              </Checkbox>
            )}
          />
          {clientRequiresCertificationType ? (
            <Text fontSize={1} color="lightText" mt={1}>
              Required by the selected Indeed Flex client.
            </Text>
          ) : null}
        </Box>

        {requiresSignedForm ? (
          <Box mt={3}>
            <Controller
              control={control}
              name="fileHandleId"
              render={({ field }) => (
                <CompanyFilePicker
                  companyId={company.companyId}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.fileHandleId?.message}
                  enabled={requiresSignedForm}
                />
              )}
            />
          </Box>
        ) : null}

        <Box mt={4}>
          <Text fontSize={2} fontWeight="600">
            Custom shift notification (optional)
          </Text>
          <Text fontSize={1} color="lightText" mt={1}>
            Applied automatically when Indeed Flex creates a shift. Delivery
            hour is relative to shift start (e.g. -2 = 2 hours before).
          </Text>
        </Box>

        <Box mt={3}>
          <Field
            label="Notification title"
            error={errors.shiftNotificationTitle?.message}
          >
            <Input
              {...register('shiftNotificationTitle')}
              placeholder="URGENT: GET PAID EARLY!!"
              maxLength={100}
            />
          </Field>
        </Box>

        <Box mt={3}>
          <Field
            label="Notification message"
            error={errors.shiftNotificationMessage?.message}
          >
            <Input
              {...register('shiftNotificationMessage')}
              placeholder="Clock in and out onsite..."
              maxLength={200}
            />
          </Field>
        </Box>

        <Box mt={3}>
          <Field
            label="Delivery hour"
            error={errors.shiftNotificationDeliveryHour?.message}
          >
            <Controller
              control={control}
              name="shiftNotificationDeliveryHour"
              render={({ field }) => (
                <DeliveryHourInput
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>
        </Box>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </ModalWithSelect>
  )
}
